import { collection, query, getDocs, doc, getDoc, where, orderBy, limit } from 'firebase/firestore'
import { db } from '../firebase'

/**
 * Device service to handle Firestore queries based on customer claims
 */
export class DeviceService {
  constructor(customerName) {
    this.customerName = customerName
    this.collectionName = `${customerName}_machines`
  }

  /**
   * Get all devices for the customer
   */
  async getAllDevices() {
    try {
      if (!this.customerName) {
        throw new Error('Customer name is required')
      }

      const devicesRef = collection(db, this.collectionName)
      const snapshot = await getDocs(devicesRef)
      
      const devices = []
      snapshot.forEach((doc) => {
        devices.push({
          id: doc.id,
          ...doc.data()
        })
      })

      return devices
    } catch (error) {
      console.error('Error fetching devices:', error)
      throw error
    }
  }

  /**
   * Get devices with specific status
   */
  async getDevicesByStatus(status) {
    try {
      if (!this.customerName) {
        throw new Error('Customer name is required')
      }

      const devicesRef = collection(db, this.collectionName)
      const q = query(devicesRef, where('status', '==', status))
      const snapshot = await getDocs(q)
      
      const devices = []
      snapshot.forEach((doc) => {
        devices.push({
          id: doc.id,
          ...doc.data()
        })
      })

      return devices
    } catch (error) {
      console.error('Error fetching devices by status:', error)
      throw error
    }
  }

  /**
   * Get a specific device by ID
   */
  async getDeviceById(deviceId) {
    try {
      if (!this.customerName) {
        throw new Error('Customer name is required')
      }

      const deviceRef = doc(db, this.collectionName, deviceId)
      const snapshot = await getDoc(deviceRef)
      
      if (snapshot.exists()) {
        return {
          id: snapshot.id,
          ...snapshot.data()
        }
      } else {
        throw new Error('Device not found')
      }
    } catch (error) {
      console.error('Error fetching device:', error)
      throw error
    }
  }

  /**
   * Get devices summary/statistics
   */
  async getDevicesSummary() {
    try {
      const allDevices = await this.getAllDevices()
      
      const summary = {
        total: allDevices.length,
        online: allDevices.filter(device => device.status === 'online').length,
        offline: allDevices.filter(device => device.status === 'offline').length,
        maintenance: allDevices.filter(device => device.status === 'maintenance').length,
        totalRevenue: allDevices.reduce((sum, device) => sum + (device.revenue || 0), 0)
      }

      return summary
    } catch (error) {
      console.error('Error fetching devices summary:', error)
      throw error
    }
  }

  /**
   * Get recent device activity
   */
  async getRecentActivity(limitCount = 10) {
    try {
      if (!this.customerName) {
        throw new Error('Customer name is required')
      }

      const devicesRef = collection(db, this.collectionName)
      const q = query(
        devicesRef, 
        orderBy('lastUpdated', 'desc'),
        limit(limitCount)
      )
      const snapshot = await getDocs(q)
      
      const devices = []
      snapshot.forEach((doc) => {
        devices.push({
          id: doc.id,
          ...doc.data()
        })
      })

      return devices
    } catch (error) {
      console.error('Error fetching recent activity:', error)
      throw error
    }
  }

  /**
   * Get legacy device IDs from shared 'device' collection
   */
  async getLegacyDeviceIds() {
    try {
      const q = query(collection(db, 'device'), where('customer', '==', this.customerName))
      const snap = await getDocs(q)
      return snap.docs.map(d => d.data().name).filter(Boolean)
    } catch (error) {
      console.error('Error fetching legacy device IDs:', error)
      return []
    }
  }

  /**
   * Get device statuses from device_state collection
   */
  async getDeviceStatuses() {
    try {
      if (!this.customerName) {
        throw new Error('Customer name is required')
      }

      const deviceStateRef = doc(db, 'device_state', this.customerName)
      const deviceStateSnap = await getDoc(deviceStateRef)
      
      if (deviceStateSnap.exists()) {
        const deviceStateData = deviceStateSnap.data()
        
        // Extract device statuses from the document
        const statuses = {}
        Object.keys(deviceStateData).forEach(key => {
          // Look for keys that match the pattern dev.{deviceId}.status
          if (key.startsWith('dev.') && key.endsWith('.status')) {
            const deviceId = key.replace('dev.', '').replace('.status', '')
            statuses[deviceId] = deviceStateData[key]
          } else if (deviceStateData[key] && typeof deviceStateData[key] === 'object' && deviceStateData[key].status) {
            // Handle direct device ID keys with status object
            statuses[key] = deviceStateData[key].status
          }
        })
        
        return statuses
      } else {
        return {}
      }
    } catch (error) {
      console.error('Error fetching device statuses:', error)
      throw error
    }
  }
}

/**
 * Factory function to create device service based on user claims
 */
export function createDeviceService(userClaims) {
  if (!userClaims || !userClaims.customer) {
    throw new Error('Valid user claims with customer name required')
  }
  
  return new DeviceService(userClaims.customer)
}